/**
 * Tide SOURCE model — the selectable tide sources, mirroring the chart-provider
 * pattern. Heights and streams are chosen independently, each labelled free vs
 * subscription and whether it works offline, so a user can run entirely on the
 * free data out of the box or plug in an Admiralty key for the official product.
 *
 * Heights: FES2014 (free, offline, the real-world default), Admiralty UK Tidal
 * API (official, needs the user's key, online only), and the RYA training data.
 * Streams: FES2014 currents (free, coarse — indication only in narrow waters),
 * an authored-diamonds slot (real transcribed chart diamonds, accurate, added per
 * area when available), Admiralty streams (paid tier), and the training diamonds.
 */
export type TideHeightSourceId = 'training' | 'fes' | 'admiralty';
export type TideStreamSourceId = 'training' | 'fes' | 'authored' | 'admiralty';
export type SourceTier = 'free' | 'subscription';

export interface TideSourceMeta<Id extends string> {
  id: Id;
  name: string;
  tier: SourceTier;
  /** Works with no network (predicted/stored locally) vs needs a live fetch. */
  offline: boolean;
  note: string;
}

export const TIDE_HEIGHT_SOURCES: TideSourceMeta<TideHeightSourceId>[] = [
  {
    id: 'fes',
    name: 'FES2014',
    tier: 'free',
    offline: true,
    note: 'Free global model, works offline. Good heights; coarse in narrow waters.',
  },
  {
    id: 'admiralty',
    name: 'Admiralty UK',
    tier: 'subscription',
    offline: false,
    note: 'Official UK heights. Needs your Admiralty API key; online only.',
  },
  {
    id: 'training',
    name: 'RYA training',
    tier: 'free',
    offline: true,
    note: 'The fictional RYA training ports. Not real-world.',
  },
];

export const TIDE_STREAM_SOURCES: TideSourceMeta<TideStreamSourceId>[] = [
  {
    id: 'fes',
    name: 'FES2014 (indication)',
    tier: 'free',
    offline: true,
    note: 'Free coarse currents. Indication only in narrow waters like the Sound of Mull.',
  },
  {
    id: 'authored',
    name: 'Authored diamonds',
    tier: 'free',
    offline: true,
    note: 'Real transcribed chart tidal diamonds. Accurate; added per area when supplied.',
  },
  {
    id: 'admiralty',
    name: 'Admiralty streams',
    tier: 'subscription',
    offline: false,
    note: 'Official UK streams. Needs an Admiralty Foundation (paid) key; online only.',
  },
  {
    id: 'training',
    name: 'RYA training',
    tier: 'free',
    offline: true,
    note: 'The fictional RYA training diamonds. Not real-world.',
  },
];
